/**
 * Diese Komponente bindet dann das jeweilige Modul ein, übergibt den User, sowie die verfügbaren Socialmediakanäle und die jeweiligen Pfade und Token.
 */

import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { Grid, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import LinkComponent from '@professocial/professocialFormComponents/FormComponents/LinkComponent'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'
import CardComponent from '@professocial/professocialFormComponents/FormComponents/CardComponent'

import getAppTheme from '../../../defaults/getAppTheme'

import getDateFormat from '../../../defaults/getDateFormat'
import { useDispatch, useSelector } from 'react-redux'
import { getHelpPostListInitiated } from '../../../store/help/help.actions'
import { HelpPost } from '@professocialApi/professocial-core-frontend-api'
import { helpPostList } from '../../../store/help/help.selector'
import { getYTId } from '@professocial/professocialFormComponents/FormComponents/util/matchSocialNetworkUrl'
import DOMPurify from 'dompurify'
import { useLocation } from 'react-router-dom'

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    padding: theme.spacing(1),
    width: '800px',
  },
  mediaItem: {
    width: '100%',
    maxHeight: 350,
    objectFit: 'contain'
  },
}))

const UserHelp: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()

  const [selected, setSelected] = useState<HelpPost | undefined>()

  const dispatchGetHelpPosts = useCallback(() => dispatch(getHelpPostListInitiated()), [dispatch])
  const helpList: HelpPost[] | undefined = useSelector(helpPostList)

  // Extract helpPostId from URL query parameters or hash
  const getHelpPostIdFromUrl = (): string | undefined => {
    // Check if the ID is in the URL hash (e.g., /user/help#123)
    if (location.hash && location.hash.length > 1) {
      return location.hash.substring(1) // Remove the # character
    }

    // Check if the ID is in query parameters (e.g., /user/help?id=123)
    const searchParams = new URLSearchParams(location.search)
    const idFromQuery = searchParams.get('id')
    if (idFromQuery) {
      return idFromQuery
    }

    return undefined
  }

  const helpPostId = getHelpPostIdFromUrl()

  useEffect(() => {
    dispatchGetHelpPosts()
  }, [])

  useEffect(() => {
    if (helpList && helpList.length > 0) {
      if (helpPostId) {
        // If a specific help post ID is provided in the URL, find and select that help post
        const helpPost = helpList.find(post => post.entityId === helpPostId)
        if (helpPost) {
          setSelected(helpPost)
        } else {
          // If the specified help post is not found, default to the first one
          setSelected(helpList[0])
        }
      } else {
        // If no specific help post ID is provided, default to the first one
        setSelected(helpList[0])
      }
    }
  }, [helpList, helpPostId])

  return (
    <>
      <Grid container spacing={2} sx={{ height: '500px' }}>
        <Grid item xs={12} sx={{ marginTop: getAppTheme().spacing(2) }}>
          <Typography variant="h3">{t('coreHelp:help')}</Typography>
        </Grid>
        <Grid container item xs={3} spacing={1} sx={{ height: '500px' }}>
          {helpList && helpList.map((post, index) => {
            return (
              <Grid item xs={12} key={index} sx={{ cursor: 'pointer' }} onClick={() => setSelected(post)}>
                <React.Fragment key={index}>
                  <CardComponent
                    class={post.entityId === selected?.entityId ? 'primaryActive' : 'primary'}
                    maxHeight={500}
                    content={
                      <>
                        <Grid container spacing={3}>
                          <Grid item xs={6}>
                            <Typography variant="h4">{post.headline}</Typography>
                            {post.date && isValidDate(post.date) &&
                              <Typography>{format(post.date, getDateFormat())}</Typography>}
                          </Grid>
                          <Grid item xs={6}>
                            {post.picture && !post.video && !post.videoUrl &&
                              <ImageComponent src={post.picture}
                                className={classes.mediaItem}
                                fallback="/src/shared/assets/images/icon-img-placeholder.svg"/>}
                            {post.video &&
                              <VideoComponent src={post.video.videoUrl}
                                className={classes.mediaItem} controls
                                fallbackThumb="/src/shared/assets/images/icon-img-placeholder.svg"/>}
                            {post.videoUrl && <ImageComponent
                              src={`https://img.youtube.com/vi/${getYTId(post.videoUrl)}/0.jpg`}
                              className={classes.mediaItem}
                              fallback="/src/shared/assets/images/icon-img-placeholder.svg"/>}
                          </Grid>
                        </Grid>
                      </>
                    }
                  />
                </React.Fragment>
              </Grid>
            )
          })}
        </Grid>
        <Grid item xs={8}>
          {selected && selected.entityId &&
            <CardComponent
              key={+selected.entityId}
              class={classes.card}
              content={
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {selected.headline &&
                      <Typography variant="h3"><strong>{selected.headline}</strong></Typography>}
                  </Grid>
                  <Grid item xs={12}>
                    {selected.date && isValidDate(selected.date) &&
                      <Typography><strong>{format(selected.date, getDateFormat())}</strong></Typography>}
                  </Grid>
                  <Grid item xs={12}>
                    {selected.video &&
                      <VideoComponent src={selected.video.videoUrl} height={350} controls
                        fallbackThumb="/src/shared/assets/images/icon-img-placeholder.svg"/>}
                    {selected.videoUrl &&
                      <VideoComponent src={selected.videoUrl} type="YT_VIDEO" height={350}
                        fallbackThumb="/src/shared/assets/images/icon-img-placeholder.svg"/>}
                  </Grid>
                  {selected.picture && !selected.video && !selected.videoUrl &&
                    <Grid item xs={12}>
                      <ImageComponent src={selected.picture} className={classes.mediaItem}
                        fallback=""/>
                    </Grid>}
                  <Grid item xs={12}>
                    {selected.text && <Typography
                      style={{ marginBottom: getAppTheme().spacing(2) }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.text, { ADD_ATTR: ['target'] }) }}
                    />}
                    {selected.linkText && selected.linkUrl && (
                      <LinkComponent text={selected.linkText} underline="always"
                        href={selected.linkUrl}/>
                    )}
                  </Grid>
                </Grid>
              }
            />}
        </Grid>
      </Grid>
    </>
  )
}
export default UserHelp

function isValidDate(date: Date) {
  return date instanceof Date && !isNaN(date.getTime())
}
